import { useEffect, useState } from 'react'
import { auth, getActiveAuth, storage } from '../config/firebase'
import { API_BASE_URL } from '../config/api'
import { ref, getDownloadURL } from 'firebase/storage'


const getAuthToken = async (): Promise<string | null> => {
  try {
    const user = getActiveAuth().currentUser
    if (!user) {
      console.warn('useSignedPhoto: No authenticated user found')
      return null
    }
    
    const token = await user.getIdToken(true)
    return token
  } catch (error) {
    console.error('useSignedPhoto: Failed to get auth token:', error)
    return null
  }
}

const extractGcsPath = (photoUrl: string): { gcsPath: string; isEmulator: boolean } | null => {
  if (!photoUrl) {
    return null
  }
  
  // Using live GCS - no emulator URLs
  
  // Handle production Google Storage URLs
  if (photoUrl.includes('storage.googleapis.com')) {
    // Get bucket name from environment or use fallback
    const bucketName = import.meta.env.VITE_GCS_UPLOAD_BUCKET || 'livewello-v4-data-bucket'
    const bucketPattern = `https://storage.googleapis.com/${bucketName}/`
    
    if (photoUrl.startsWith(bucketPattern)) {
      const gcsPath = photoUrl.replace(bucketPattern, '')
      return { gcsPath, isEmulator: false }
    }
  }
  
  return null
}

export const useSignedPhoto = (photoUrl?: string, fallback?: string): string | undefined => {
  const [url, setUrl] = useState<string | undefined>(
    photoUrl && photoUrl.startsWith('http') ? photoUrl : undefined
  )

  useEffect(() => {
    if (!photoUrl) return
    
    // If it's already a full HTTP URL and not from storage services, use it directly
    if (photoUrl.startsWith('http') && !photoUrl.includes('storage.googleapis.com')) {
      setUrl(photoUrl)
      return
    }
    
    // Extract GCS path and determine if it's emulator
    let pathInfo: { gcsPath: string; isEmulator: boolean } | null = null
    
    if (photoUrl.startsWith('http')) {
      // Extract GCS path from full URL
      pathInfo = extractGcsPath(photoUrl)
    } else {
      // Assume it's already a GCS path (not emulator)
      pathInfo = { gcsPath: photoUrl, isEmulator: false }
    }
    
    if (!pathInfo) {
      console.warn('useSignedPhoto: Could not extract GCS path from:', photoUrl)
      return
    }

    const { gcsPath, isEmulator } = pathInfo

    // Using live GCS - no emulator mode needed

    // For production, use backend signed URL generation
    ;(async () => {
      try {
        // Get authentication token
        const token = await getAuthToken()
        if (!token) {
          console.warn('useSignedPhoto: No auth token available, using fallback')
          return
        }

        const res = await fetch(`${API_BASE_URL}/api/v1/uploads/generate-download-url`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ gcs_path: gcsPath })
        })
        
        if (!res.ok) {
          console.warn(`useSignedPhoto: API request failed with status ${res.status}`)
          return
        }
        
        const data = await res.json()
        if (data.download_url) {
          setUrl(data.download_url)
        }
      } catch (error) {
        console.error('useSignedPhoto: Failed to generate signed URL:', error)
        // ignore fetch errors, keep fallback
      }
    })()
  }, [photoUrl])

  return url || fallback
} 
